import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  Btn, Badge, StatusBadge, Card, CardHeader, Table, Tr, Td,
  FormGroup, Input, Select, Textarea, Modal, StatCard, Alert,
  Spinner, SectionDivider, fmtAmt, fmtDate
} from '../components/ui';
import {
  sbAPI, grnAPI, reportsAPI, containerAPI, soAPI, partiesAPI, poAPI
} from '../utils/api';
import api from '../utils/api';

function useData(fetchFn, deps = []) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(() => {
    setLoading(true);
    fetchFn()
      .then(r => setData(r.data))
      .catch(() => toast.error('Load failed'))
      .finally(() => setLoading(false));
  }, deps); // eslint-disable-line
  useEffect(() => { reload(); }, [reload]);
  return [data, loading, reload];
}

// ══════════════════════════════════════════════════════════
// ALERTS PAGE
// ══════════════════════════════════════════════════════════
export function Alerts() {
  const [data, loading] = useData(() => api.get('/dashboard'));

  const alerts = [
    { type: 'danger', icon: '🔴', title: 'FEMA Overdue', msg: 'Export proceeds > 180 days pending repatriation. RBI reporting required immediately.', action: 'View Invoices' },
    { type: 'danger', icon: '🔴', title: 'Customs Query Pending', msg: 'Bill of Entry under query at Mundra Port. Respond within 48 hours via ICEGATE.', action: 'Resolve' },
    { type: 'warn',   icon: '🟡', title: 'IEC Renewal Due', msg: 'IEC No. 0512034567 expires in 45 days. Renew on DGFT portal before expiry.', action: 'Renew' },
    { type: 'warn',   icon: '🟡', title: 'MEIS Scrip Expiring', msg: 'MEIS Scrip #SC2025-04-001 expires in 10 days. ₹3.2L balance. Utilise or transfer.', action: 'Utilise' },
    { type: 'warn',   icon: '🟡', title: 'E-Way Bill Expired', msg: 'EWB-241081934 expired for INV-0409. Extend or cancel to avoid GST penalty.', action: 'Extend' },
    { type: 'info',   icon: '🔵', title: 'GSTR-1 Due in 5 Days', msg: 'GSTR-1 for current month is due on 11th. Upload invoices to GST portal.', action: 'Go to GST' },
    { type: 'info',   icon: '🔵', title: 'Advance Tax Due', msg: 'Q1 advance tax due 15 June. Estimated ₹18.2L. Set bank reminder.', action: 'View' },
    { type: 'info',   icon: '🔵', title: 'Container ETA Tomorrow', msg: 'Container HLCU4489221 arriving Hamburg tomorrow. Notify buyer Muller GmbH.', action: 'Track' },
  ];

  const colorMap = { danger: 'bg-red-50 border-red-200 text-red-800', warn: 'bg-amber-50 border-amber-200 text-amber-800', info: 'bg-blue-50 border-blue-200 text-blue-800' };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-black text-slate-800">🔔 Alerts & Tasks</h1>
        <p className="text-sm text-slate-400 mt-0.5">Compliance deadlines, expiries, action items</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard icon="🔴" label="Urgent" value="2" color="red" />
        <StatCard icon="🟡" label="Warning" value="3" color="amber" />
        <StatCard icon="🔵" label="Info" value="3" color="blue" />
        <StatCard icon="✅" label="Resolved Today" value="0" color="green" />
      </div>
      <div className="space-y-3">
        {alerts.map((a, i) => (
          <div key={i} className={`flex items-start gap-3 p-4 rounded-xl border ${colorMap[a.type]}`}>
            <span className="text-xl flex-shrink-0 mt-0.5">{a.icon}</span>
            <div className="flex-1">
              <div className="font-bold text-sm">{a.title}</div>
              <div className="text-sm mt-0.5 opacity-80">{a.msg}</div>
            </div>
            <Btn size="sm" variant="outline" className="flex-shrink-0 text-xs">{a.action}</Btn>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SHIPPING BILLS
// ══════════════════════════════════════════════════════════
export function ShippingBills() {
  const [data, loading, reload] = useData(() => api.get('/shipping-bills'));
  const [soData]   = useData(() => soAPI.list());
  const [contData] = useData(() => containerAPI.list());
  const [modal, setModal]   = useState(false);
  const [form, setForm]     = useState({ sb_number: '', sb_date: new Date().toISOString().split('T')[0], so_id: '', container_id: '', port_code: 'INJNP', drawback_amount: '', rodtep_amount: '', leo_date: '', status: 'filed', remarks: '' });
  const [saving, setSaving] = useState(false);

  const bills  = data?.shipping_bills || [];
  const sos    = soData?.sales_orders || [];
  const conts  = contData?.containers || [];

  const save = async () => {
    if (!form.sb_number) return toast.error('SB number required');
    setSaving(true);
    try {
      await api.post('/shipping-bills', form);
      toast.success('Shipping Bill saved!');
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const totalDrawback = bills.reduce((s, r) => s + parseFloat(r.drawback_amount || 0), 0);
  const totalRodtep   = bills.reduce((s, r) => s + parseFloat(r.rodtep_amount || 0), 0);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📋 Shipping Bills</h1><p className="text-sm text-slate-400">ICEGATE filed SBs — Drawback & RoDTEP tracking</p></div>
        <div className="ml-auto"><Btn onClick={() => setModal(true)}>+ New Shipping Bill</Btn></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatCard icon="📋" label="Total SBs Filed" value={bills.length} color="blue" />
        <StatCard icon="💰" label="Drawback Earned" value={fmtAmt(totalDrawback)} color="green" />
        <StatCard icon="📜" label="RoDTEP Credit" value={fmtAmt(totalRodtep)} color="amber" />
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['SB No.', 'Date', 'SO Ref', 'Container', 'Port', 'Drawback', 'RoDTEP', 'LEO Date', 'Status', 'Actions']}>
            {bills.map(r => (
              <Tr key={r.id}>
                <Td><span className="font-mono font-bold text-blue-600">{r.sb_number}</span></Td>
                <Td>{fmtDate(r.sb_date)}</Td>
                <Td>{r.so_number ? <Badge color="blue">{r.so_number}</Badge> : '—'}</Td>
                <Td mono>{r.container_number || '—'}</Td>
                <Td>{r.port_code}</Td>
                <Td right><span className="font-bold text-green-600">{fmtAmt(r.drawback_amount)}</span></Td>
                <Td right><span className="font-bold text-amber-600">{fmtAmt(r.rodtep_amount)}</span></Td>
                <Td>{r.leo_date ? fmtDate(r.leo_date) : <Badge color="yellow">Pending</Badge>}</Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td><Btn size="sm" variant="ghost">View</Btn></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title="New Shipping Bill" size="lg"
        footer={<><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>✓ Save SB</Btn></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="SB Number" required><Input value={form.sb_number} onChange={e => setForm(f => ({ ...f, sb_number: e.target.value }))} placeholder="e.g. 8821044" /></FormGroup>
          <FormGroup label="SB Date"><Input type="date" value={form.sb_date} onChange={e => setForm(f => ({ ...f, sb_date: e.target.value }))} /></FormGroup>
          <FormGroup label="Linked Sales Order">
            <Select value={form.so_id} onChange={e => setForm(f => ({ ...f, so_id: e.target.value }))}>
              <option value="">— Select SO —</option>
              {sos.map(s => <option key={s.id} value={s.id}>{s.so_number} — {s.party_name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Linked Container">
            <Select value={form.container_id} onChange={e => setForm(f => ({ ...f, container_id: e.target.value }))}>
              <option value="">— Select Container —</option>
              {conts.map(c => <option key={c.id} value={c.id}>{c.container_number}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Port Code">
            <Select value={form.port_code} onChange={e => setForm(f => ({ ...f, port_code: e.target.value }))}>
              <option value="INJNP">INJNP — JNPT Mumbai</option>
              <option value="INMAA">INMAA — Chennai</option>
              <option value="INMUN">INMUN — Mundra</option>
              <option value="INCCU">INCCU — Kolkata</option>
              <option value="INCOK">INCOK — Cochin</option>
            </Select>
          </FormGroup>
          <FormGroup label="Status">
            <Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="filed">Filed</option>
              <option value="query">Under Query</option>
              <option value="leo">LEO Granted</option>
              <option value="drawback_credited">Drawback Credited</option>
            </Select>
          </FormGroup>
          <FormGroup label="Drawback Amount (₹)"><Input type="number" value={form.drawback_amount} onChange={e => setForm(f => ({ ...f, drawback_amount: e.target.value }))} placeholder="0.00" /></FormGroup>
          <FormGroup label="RoDTEP Amount (₹)"><Input type="number" value={form.rodtep_amount} onChange={e => setForm(f => ({ ...f, rodtep_amount: e.target.value }))} placeholder="0.00" /></FormGroup>
          <FormGroup label="LEO Date"><Input type="date" value={form.leo_date} onChange={e => setForm(f => ({ ...f, leo_date: e.target.value }))} /></FormGroup>
          <FormGroup label="LEO Time"><Input type="time" value={form.leo_time || ''} onChange={e => setForm(f => ({ ...f, leo_time: e.target.value }))} /></FormGroup>
        </div>
        <div className="mt-3"><FormGroup label="Remarks"><Textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} /></FormGroup></div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// GST / ITC
// ══════════════════════════════════════════════════════════
export function GST() {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear]   = useState(new Date().getFullYear());
  const [data, loading, reload] = useData(() => api.get(`/gst?action=summary&month=${month}&year=${year}`), [month, year]);
  const [itcData, itcLoading]   = useData(() => api.get('/gst?action=itc_list'));
  const [expData, expLoading]   = useData(() => api.get('/gst?action=export_list'));

  const s = data || {};

  const filingStatus = [
    { form: 'GSTR-1', period: 'Previous Month', status: 'Filed ✓', color: 'green' },
    { form: 'GSTR-3B', period: 'Previous Month', status: 'Filed ✓', color: 'green' },
    { form: 'GSTR-1', period: 'Current Month', status: 'Due 11th', color: 'yellow' },
    { form: 'GSTR-3B', period: 'Current Month', status: 'Due 20th', color: 'yellow' },
    { form: 'GSTR-9 Annual', period: 'FY 2024-25', status: 'In Progress', color: 'blue' },
    { form: 'LUT (Zero Rated)', period: 'FY 2025-26', status: 'Active ✓', color: 'green' },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🧮 GST / ITC Management</h1><p className="text-sm text-slate-400">Returns, Input Tax Credit, IGST Refunds, LUT</p></div>
        <div className="flex gap-2 ml-auto">
          <Select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ width: 100 }}>
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </Select>
          <Select value={year} onChange={e => setYear(Number(e.target.value))} style={{ width: 90 }}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
          <StatCard icon="📤" label="Output GST" value={fmtAmt(s.output_gst)} color="blue" />
          <StatCard icon="📥" label="Input Tax Credit (ITC)" value={fmtAmt(s.itc)} color="green" />
          <StatCard icon="0️⃣" label="Zero-Rated Exports" value={fmtAmt(s.zero_rated)} color="amber" />
          <StatCard icon="💸" label="Net GST Payable" value={fmtAmt(s.net_payable)} color={parseFloat(s.net_payable) > 0 ? 'red' : 'green'} sub={parseFloat(s.net_payable) <= 0 ? 'Credit Excess' : ''} />
          <StatCard icon="🔄" label="IGST Refund Pending" value={fmtAmt(s.igst_refund)} color="purple" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <CardHeader title="GSTR Filing Status" />
          <div className="divide-y divide-slate-100">
            {filingStatus.map((f, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3">
                <div>
                  <span className="font-bold text-sm text-slate-800">{f.form}</span>
                  <span className="text-xs text-slate-400 ml-2">({f.period})</span>
                </div>
                <Badge color={f.color}>{f.status}</Badge>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <CardHeader title="ITC Summary" />
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm border-b border-slate-100 pb-2"><span className="text-slate-400">GSTR-2B ITC Available</span><span className="font-bold">{fmtAmt((parseFloat(s.itc || 0) * 1.1).toFixed(2))}</span></div>
            <div className="flex justify-between text-sm border-b border-slate-100 pb-2"><span className="text-slate-400">ITC Claimed in 3B</span><span className="font-bold">{fmtAmt(s.itc)}</span></div>
            <div className="flex justify-between text-sm border-b border-slate-100 pb-2"><span className="text-slate-400">Ineligible ITC (Sec 17(5))</span><span className="font-bold text-red-500">{fmtAmt(0)}</span></div>
            <div className="flex justify-between text-sm font-bold"><span>Net ITC Available</span><span className="text-green-600">{fmtAmt(s.itc)}</span></div>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader title="Zero-Rated Exports (Under LUT)" />
        {expLoading ? <Spinner /> : (
          <Table headers={['Invoice No.', 'Date', 'Buyer', 'Value', 'Currency', 'INR Value', 'GST Type']}>
            {(expData?.export_list || []).map((r, i) => (
              <Tr key={i}>
                <Td mono>{r.invoice_number}</Td>
                <Td>{fmtDate(r.invoice_date)}</Td>
                <Td>{r.buyer_name}</Td>
                <Td right><span className="font-bold">{parseFloat(r.grand_total).toLocaleString()}</span></Td>
                <Td>{r.currency}</Td>
                <Td right>{fmtAmt(r.amount_inr)}</Td>
                <Td><Badge color="green">ZERO LUT</Badge></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// DGFT LICENCES
// ══════════════════════════════════════════════════════════
export function DGFT() {
  const [data, loading, reload] = useData(() => api.get('/dgft'));
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ licence_type: '', licence_number: '', issue_date: '', expiry_date: '', value: '', balance: '', currency: 'INR', status: 'active', notes: '' });
  const [saving, setSaving] = useState(false);

  const licences = data?.licences || [];
  const expiringSoon = licences.filter(l => l.days_to_expiry <= 30 && l.days_to_expiry >= 0);

  const save = async () => {
    if (!form.licence_type) return toast.error('Licence type required');
    setSaving(true);
    try {
      await api.post('/dgft', form);
      toast.success('Licence added!');
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const licenceTypes = ['IEC', 'EPCG', 'Advance Authorisation', 'MEIS Scrip', 'RoDTEP', 'SEIS', 'DFIA', 'EPCG (Post Export)', 'FPS', 'Other'];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">📜 DGFT Licences</h1><p className="text-sm text-slate-400">IEC, EPCG, Advance Auth, MEIS, RoDTEP, SEIS</p></div>
        <div className="ml-auto"><Btn onClick={() => setModal(true)}>+ Add Licence</Btn></div>
      </div>

      {expiringSoon.length > 0 && (
        <Alert type="warn">⚠️ <strong>{expiringSoon.length} licence(s)</strong> expiring within 30 days: {expiringSoon.map(l => l.licence_type).join(', ')}</Alert>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard icon="✅" label="Active" value={licences.filter(l => l.status === 'active').length} color="green" />
        <StatCard icon="⚠️" label="Expiring Soon" value={expiringSoon.length} color="amber" />
        <StatCard icon="❌" label="Expired" value={licences.filter(l => l.status === 'expired').length} color="red" />
        <StatCard icon="📜" label="Total Licences" value={licences.length} color="blue" />
      </div>

      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['Licence Type', 'Licence No.', 'Issue Date', 'Expiry Date', 'Value', 'Balance', 'Days Left', 'Status', 'Actions']}>
            {licences.map(r => (
              <Tr key={r.id}>
                <Td><span className="font-bold">{r.licence_type}</span></Td>
                <Td mono>{r.licence_number || '—'}</Td>
                <Td>{fmtDate(r.issue_date)}</Td>
                <Td>{fmtDate(r.expiry_date)}</Td>
                <Td right>{r.currency} {parseFloat(r.value || 0).toLocaleString()}</Td>
                <Td right><span className="font-bold text-green-600">{r.currency} {parseFloat(r.balance || 0).toLocaleString()}</span></Td>
                <Td>
                  <span className={`font-bold ${r.days_to_expiry <= 0 ? 'text-red-600' : r.days_to_expiry <= 30 ? 'text-amber-600' : 'text-green-600'}`}>
                    {r.days_to_expiry <= 0 ? 'Expired' : `${r.days_to_expiry}d`}
                  </span>
                </Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td><Btn size="sm" variant="ghost">Edit</Btn></Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title="Add DGFT Licence" size="md"
        footer={<><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>Save Licence</Btn></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="Licence Type" required>
            <Select value={form.licence_type} onChange={e => setForm(f => ({ ...f, licence_type: e.target.value }))}>
              <option value="">— Select Type —</option>
              {licenceTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Licence Number"><Input value={form.licence_number} onChange={e => setForm(f => ({ ...f, licence_number: e.target.value }))} /></FormGroup>
          <FormGroup label="Issue Date"><Input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} /></FormGroup>
          <FormGroup label="Expiry Date"><Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} /></FormGroup>
          <FormGroup label="Total Value"><Input type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} /></FormGroup>
          <FormGroup label="Available Balance"><Input type="number" value={form.balance} onChange={e => setForm(f => ({ ...f, balance: e.target.value }))} /></FormGroup>
          <FormGroup label="Currency"><Select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}><option>INR</option><option>USD</option><option>EUR</option></Select></FormGroup>
          <FormGroup label="Status"><Select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}><option value="active">Active</option><option value="expired">Expired</option><option value="utilised">Fully Utilised</option><option value="cancelled">Cancelled</option></Select></FormGroup>
        </div>
        <div className="mt-3"><FormGroup label="Notes"><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></FormGroup></div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// CUSTOMS / CHA
// ══════════════════════════════════════════════════════════
export function Customs() {
  const [sbData, sbLoading]   = useData(() => api.get('/shipping-bills'));
  const [contData, contLoading] = useData(() => containerAPI.list());

  const bills  = sbData?.shipping_bills  || [];
  const conts  = contData?.containers    || [];
  const pending = bills.filter(b => b.status === 'filed' || b.status === 'query');
  const leoGranted = bills.filter(b => b.status === 'leo' || b.status === 'drawback_credited');

  return (
    <div>
      <div className="mb-5"><h1 className="text-xl font-black text-slate-800">🛃 Customs & CHA</h1><p className="text-sm text-slate-400">Shipping Bills, Bill of Entry, LEO, Examination</p></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard icon="📋" label="Total SBs" value={bills.length} color="blue" />
        <StatCard icon="⏳" label="Pending LEO" value={pending.length} color="amber" />
        <StatCard icon="✅" label="LEO Granted" value={leoGranted.length} color="green" />
        <StatCard icon="🚢" label="Active Containers" value={conts.filter(c => ['booked','stuffed','leo','sailed','in_transit'].includes(c.status)).length} color="purple" />
      </div>

      <Alert type="info">💡 <strong>Customs Workflow:</strong> File Shipping Bill on ICEGATE → Cargo Examination → Let Export Order (LEO) → Vessel Sailing → B/L Collection → Drawback Credit</Alert>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Pending LEO / Under Query" />
          {sbLoading ? <Spinner /> : (
            <Table headers={['SB No.', 'SO Ref', 'Port', 'Status', 'Action']}>
              {pending.map(r => (
                <Tr key={r.id}>
                  <Td mono>{r.sb_number}</Td>
                  <Td>{r.so_number ? <Badge color="blue">{r.so_number}</Badge> : '—'}</Td>
                  <Td>{r.port_code}</Td>
                  <Td><StatusBadge status={r.status} /></Td>
                  <Td>{r.status === 'query' ? <Btn size="sm" variant="danger">Resolve</Btn> : <Btn size="sm" variant="ghost">View</Btn>}</Td>
                </Tr>
              ))}
            </Table>
          )}
        </Card>

        <Card>
          <CardHeader title="Container Customs Status" />
          {contLoading ? <Spinner /> : (
            <Table headers={['Container', 'Vessel', 'Status', 'LEO', 'BL']}>
              {conts.slice(0, 8).map(c => (
                <Tr key={c.id}>
                  <Td mono>{c.container_number}</Td>
                  <Td>{c.vessel_name || '—'}</Td>
                  <Td><StatusBadge status={c.status} /></Td>
                  <Td>{c.status === 'leo' || c.status === 'sailed' || c.status === 'in_transit' || c.status === 'delivered' ? <Badge color="green">✓</Badge> : <Badge color="yellow">Pending</Badge>}</Td>
                  <Td mono>{c.bl_number || '—'}</Td>
                </Tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// E-WAY BILLS
// ══════════════════════════════════════════════════════════
export function EwayBills() {
  const [data, loading, reload] = useData(() => api.get('/eway-bills'));
  const [soData] = useData(() => soAPI.list());
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ ewb_number: '', invoice_ref: '', so_id: '', from_place: '', to_place: '', vehicle_no: '', transporter: '', value: '', generated_on: new Date().toISOString().slice(0, 16), valid_till: '' });
  const [saving, setSaving] = useState(false);

  const bills = data?.eway_bills || [];
  const sos   = soData?.sales_orders || [];

  const save = async () => {
    if (!form.invoice_ref) return toast.error('Invoice reference required');
    setSaving(true);
    try {
      await api.post('/eway-bills', form);
      toast.success('E-Way Bill created!');
      setModal(false); reload();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div><h1 className="text-xl font-black text-slate-800">🛣️ E-Way Bills</h1><p className="text-sm text-slate-400">Generate, extend, cancel EWBs for goods movement</p></div>
        <div className="ml-auto"><Btn onClick={() => setModal(true)}>+ Generate EWB</Btn></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        <StatCard icon="✅" label="Active" value={bills.filter(b => b.status === 'active').length} color="green" />
        <StatCard icon="❌" label="Expired" value={bills.filter(b => b.status === 'expired').length} color="red" />
        <StatCard icon="📊" label="Total" value={bills.length} color="blue" />
      </div>
      <Card>
        {loading ? <Spinner /> : (
          <Table headers={['EWB No.', 'Invoice Ref', 'From', 'To', 'Vehicle', 'Value', 'Valid Till', 'Status', 'Actions']}>
            {bills.map(r => (
              <Tr key={r.id}>
                <Td mono>{r.ewb_number || '—'}</Td>
                <Td mono>{r.invoice_ref}</Td>
                <Td>{r.from_place}</Td>
                <Td>{r.to_place}</Td>
                <Td mono>{r.vehicle_no || '—'}</Td>
                <Td right>{fmtAmt(r.value)}</Td>
                <Td>{r.valid_till ? new Date(r.valid_till).toLocaleString('en-IN') : '—'}</Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td>
                  {r.status === 'active' && <Btn size="sm" variant="warn">Extend</Btn>}
                  {r.status === 'active' && <Btn size="sm" variant="ghost" className="ml-1">Cancel</Btn>}
                  {r.status !== 'active' && <Btn size="sm" variant="ghost">View</Btn>}
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal} onClose={() => setModal(false)} title="Generate E-Way Bill" size="lg"
        footer={<><Btn variant="ghost" onClick={() => setModal(false)}>Cancel</Btn><Btn loading={saving} onClick={save}>✓ Generate EWB</Btn></>}>
        <Alert type="info">ℹ️ EWB is mandatory for goods movement exceeding ₹50,000 in value.</Alert>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormGroup label="EWB Number (from GST Portal)"><Input value={form.ewb_number} onChange={e => setForm(f => ({ ...f, ewb_number: e.target.value }))} placeholder="e.g. 241082441" /></FormGroup>
          <FormGroup label="Invoice Reference" required><Input value={form.invoice_ref} onChange={e => setForm(f => ({ ...f, invoice_ref: e.target.value }))} placeholder="GST Invoice No." /></FormGroup>
          <FormGroup label="Linked SO">
            <Select value={form.so_id} onChange={e => setForm(f => ({ ...f, so_id: e.target.value }))}>
              <option value="">— Select SO —</option>
              {sos.map(s => <option key={s.id} value={s.id}>{s.so_number} — {s.party_name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup label="Taxable Value (₹)"><Input type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} /></FormGroup>
          <FormGroup label="From Place"><Input value={form.from_place} onChange={e => setForm(f => ({ ...f, from_place: e.target.value }))} placeholder="e.g. Andheri East, Mumbai" /></FormGroup>
          <FormGroup label="To Place"><Input value={form.to_place} onChange={e => setForm(f => ({ ...f, to_place: e.target.value }))} placeholder="e.g. JNPT, Nhava Sheva" /></FormGroup>
          <FormGroup label="Vehicle Number"><Input value={form.vehicle_no} onChange={e => setForm(f => ({ ...f, vehicle_no: e.target.value }))} placeholder="e.g. MH-04-AB-9812" /></FormGroup>
          <FormGroup label="Transporter"><Input value={form.transporter} onChange={e => setForm(f => ({ ...f, transporter: e.target.value }))} placeholder="Transporter name / GSTIN" /></FormGroup>
          <FormGroup label="Generated On"><Input type="datetime-local" value={form.generated_on} onChange={e => setForm(f => ({ ...f, generated_on: e.target.value }))} /></FormGroup>
          <FormGroup label="Valid Till"><Input type="datetime-local" value={form.valid_till} onChange={e => setForm(f => ({ ...f, valid_till: e.target.value }))} /></FormGroup>
        </div>
      </Modal>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MIS REPORTS
// ══════════════════════════════════════════════════════════
export function MIS() {
  const [tab, setTab] = useState('summary');
  const [from, setFrom] = useState(new Date().getFullYear() + '-04-01');
  const [to, setTo]     = useState(new Date().toISOString().split('T')[0]);
  const [data, loading, reload] = useData(() => api.get(`/mis?type=${tab}&from=${from}&to=${to}`), [tab, from, to]);

  const tabs = [
    { key: 'summary',      label: '📊 Summary'      },
    { key: 'monthly',      label: '📅 Monthly'       },
    { key: 'country_wise', label: '🌍 Country Wise'  },
    { key: 'product_wise', label: '📦 Product Wise'  },
    { key: 'profitability',label: '💹 Profitability' },
  ];

  const s = data?.summary || {};

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-black text-slate-800">📈 MIS Reports</h1>
        <p className="text-sm text-slate-400">Export performance, country analysis, profitability</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200 flex-wrap">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${tab === t.key ? 'bg-white shadow text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 ml-auto">
          <FormGroup label="From"><Input type="date" value={from} onChange={e => setFrom(e.target.value)} /></FormGroup>
          <FormGroup label="To"><Input type="date" value={to} onChange={e => setTo(e.target.value)} /></FormGroup>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <>
          {tab === 'summary' && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                <StatCard icon="💹" label="Export Revenue (INR)" value={fmtAmt(s.total_export_inr)} color="green" />
                <StatCard icon="🛒" label="Sales Orders" value={s.total_so_count || 0} color="blue" />
                <StatCard icon="📦" label="Purchase Cost" value={fmtAmt(s.total_po_value)} color="purple" />
                <StatCard icon="📑" label="Bills Paid" value={fmtAmt(s.total_bills_value)} color="red" />
                <StatCard icon="🧮" label="ITC Claimed" value={fmtAmt(s.total_itc)} color="amber" />
                <StatCard icon="💰" label="Drawback + RoDTEP" value={fmtAmt((parseFloat(s.drawback_earned || 0) + parseFloat(s.rodtep_earned || 0)).toFixed(2))} color="green" />
              </div>
              <Card>
                <CardHeader title="Period Summary" subtitle={`${fmtDate(from)} — ${fmtDate(to)}`} />
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    ['Total Export Revenue', fmtAmt(s.total_export_inr)],
                    ['Total Purchase Cost', fmtAmt(s.total_po_value)],
                    ['Gross Profit', fmtAmt((parseFloat(s.total_export_inr || 0) - parseFloat(s.total_po_value || 0)).toFixed(2))],
                    ['Drawback Earned', fmtAmt(s.drawback_earned)],
                    ['RoDTEP Credit', fmtAmt(s.rodtep_earned)],
                    ['GST ITC Claimed', fmtAmt(s.total_itc)],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-500">{label}</span>
                      <span className="text-sm font-bold text-slate-800">{value}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}

          {tab === 'monthly' && (
            <Card>
              <CardHeader title="Monthly Export Trend" />
              <Table headers={['Month', 'Invoices', 'Export Revenue (₹)', 'Foreign Value']}>
                {(data?.monthly || []).map((r, i) => (
                  <Tr key={i}>
                    <Td><span className="font-bold">{r.mon_label}</span></Td>
                    <Td>{r.invoice_count}</Td>
                    <Td right><span className="font-bold text-green-600">{fmtAmt(r.total_inr)}</span></Td>
                    <Td right>{parseFloat(r.total_foreign || 0).toLocaleString()}</Td>
                  </Tr>
                ))}
              </Table>
            </Card>
          )}

          {tab === 'country_wise' && (
            <Card>
              <CardHeader title="Country-wise Export" />
              <Table headers={['Country', 'Invoices', 'Revenue (INR)', 'Share']}>
                {(data?.country_wise || []).map((r, i) => {
                  const total = data.country_wise.reduce((s, x) => s + parseFloat(x.total_inr || 0), 0);
                  const pct = total > 0 ? ((parseFloat(r.total_inr || 0) / total) * 100).toFixed(1) : '0';
                  return (
                    <Tr key={i}>
                      <Td><span className="font-bold">{r.country}</span></Td>
                      <Td>{r.invoice_count}</Td>
                      <Td right><span className="font-bold text-green-600">{fmtAmt(r.total_inr)}</span></Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-bold">{pct}%</span>
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </Table>
            </Card>
          )}

          {tab === 'product_wise' && (
            <Card>
              <CardHeader title="Product-wise Export" />
              <Table headers={['Description', 'HS Code', 'Total Qty', 'UoM', 'Total Amount']}>
                {(data?.product_wise || []).map((r, i) => (
                  <Tr key={i}>
                    <Td><span className="font-semibold">{r.description}</span></Td>
                    <Td mono>{r.hs_code}</Td>
                    <Td right>{parseFloat(r.total_qty || 0).toLocaleString()}</Td>
                    <Td>{r.uom}</Td>
                    <Td right><span className="font-bold">{parseFloat(r.total_amount || 0).toLocaleString()}</span></Td>
                  </Tr>
                ))}
              </Table>
            </Card>
          )}

          {tab === 'profitability' && (
            <Card>
              <CardHeader title="Profitability by Sales Order" />
              <Table headers={['SO No.', 'Buyer', 'Currency', 'SO Value', 'PO Cost (₹)', 'Incentives (₹)', 'Margin (₹)', 'Margin %']}>
                {(data?.profitability || []).map((r, i) => {
                  const marginPct = parseFloat(r.so_inr) > 0 ? ((parseFloat(r.margin || 0) / parseFloat(r.so_inr)) * 100).toFixed(1) : '0';
                  return (
                    <Tr key={i}>
                      <Td mono>{r.so_number}</Td>
                      <Td>{r.buyer_name}</Td>
                      <Td>{r.currency}</Td>
                      <Td right>{parseFloat(r.so_value || 0).toLocaleString()}</Td>
                      <Td right>{fmtAmt(r.po_cost)}</Td>
                      <Td right><span className="text-green-600 font-semibold">{fmtAmt(r.incentives)}</span></Td>
                      <Td right><span className={parseFloat(r.margin) >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{fmtAmt(r.margin)}</span></Td>
                      <Td right><span className={parseFloat(marginPct) >= 15 ? 'text-green-600 font-bold' : parseFloat(marginPct) >= 0 ? 'text-amber-600 font-bold' : 'text-red-600 font-bold'}>{marginPct}%</span></Td>
                    </Tr>
                  );
                })}
              </Table>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// DOCUMENTS (Trade Documents Quick Access)
// ══════════════════════════════════════════════════════════
export function Documents() {
  const docTypes = [
    { icon: '🧾', name: 'Commercial Invoice',     desc: 'Export invoice with HS codes and CIF value',    color: 'blue'   },
    { icon: '📦', name: 'Packing List',            desc: 'Container-wise, carton-wise packing details',   color: 'green'  },
    { icon: '🚢', name: 'Bill of Lading',          desc: 'Original BL / surrender copy from carrier',     color: 'purple' },
    { icon: '📋', name: 'Shipping Bill',           desc: 'ICEGATE SB with drawback & RoDTEP',             color: 'amber'  },
    { icon: '🌐', name: 'Certificate of Origin',   desc: 'GSP Form A — EIC / APEDA issued',               color: 'green'  },
    { icon: '🌿', name: 'Phytosanitary Cert.',     desc: 'NPPO India — ISPM 12 format',                    color: 'green'  },
    { icon: '🏦', name: 'Letter of Credit',        desc: 'Import/Export LC — HDFC, SBI, ICICI',           color: 'blue'   },
    { icon: '📑', name: 'Bill of Exchange',        desc: 'Draft for LC negotiation',                      color: 'gray'   },
    { icon: '💱', name: 'e-BRC Certificate',       desc: 'Bank Realisation Certificate — FEMA',           color: 'green'  },
    { icon: '🔬', name: 'Inspection Certificate',  desc: 'SGS / Bureau Veritas / EIC quality cert',       color: 'blue'   },
    { icon: '🛡️', name: 'Insurance Certificate',  desc: 'Marine Insurance — ICC(A) all risks',            color: 'amber'  },
    { icon: '🛃', name: 'Bill of Entry',           desc: 'Import BE — BCD + IGST calculations',           color: 'red'    },
    { icon: '✈️', name: 'Airway Bill (AWB)',       desc: 'IATA format air cargo document',                color: 'blue'   },
    { icon: '🛣️', name: 'E-Way Bill',             desc: 'GSTN EWB for goods movement >₹50K',             color: 'amber'  },
    { icon: '🧮', name: 'GST Tax Invoice',         desc: 'Zero-rated LUT export invoice',                 color: 'green'  },
  ];

  const colorMap = {
    blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
    green: 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50',
    purple: 'border-violet-200 hover:border-violet-400 hover:bg-violet-50',
    amber: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50',
    red: 'border-red-200 hover:border-red-400 hover:bg-red-50',
    gray: 'border-slate-200 hover:border-slate-400 hover:bg-slate-50',
  };

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-black text-slate-800">📄 Trade Documents</h1>
        <p className="text-sm text-slate-400">Generate, manage and print all shipping & compliance documents</p>
      </div>
      <Alert type="info">💡 Documents are auto-populated from linked Sales Orders, Containers, and Invoices. Open any document to preview or print.</Alert>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {docTypes.map((doc, i) => (
          <div key={i}
            className={`bg-white border rounded-xl p-4 cursor-pointer transition-all ${colorMap[doc.color]} group`}
            onClick={() => toast.success(`Opening ${doc.name} template...`)}>
            <div className="flex items-start gap-3">
              <span className="text-2xl">{doc.icon}</span>
              <div className="flex-1">
                <div className="font-bold text-sm text-slate-800 group-hover:text-blue-700">{doc.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">{doc.desc}</div>
              </div>
              <span className="text-slate-300 group-hover:text-blue-400 text-sm">→</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// SETTINGS
// ══════════════════════════════════════════════════════════
export function Settings() {
  const [tab, setTab] = useState('company');
  const [compData, compLoading] = useData(() => api.get('/settings?action=company'));
  const [usersData, usersLoading] = useData(() => api.get('/settings?action=users'));
  const [company, setCompany] = useState({});
  const [saving, setSaving]   = useState(false);
  const [pwForm, setPwForm]   = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [userModal, setUserModal] = useState(false);

  useEffect(() => { if (compData?.company) setCompany(compData.company); }, [compData]);

  const saveCompany = async () => {
    setSaving(true);
    try {
      await api.post('/settings?action=company', company);
      toast.success('Company settings saved!');
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm_password) return toast.error('Passwords do not match');
    if (pwForm.new_password.length < 8) return toast.error('Minimum 8 characters required');
    setSaving(true);
    try {
      await api.post('/settings?action=change_password', pwForm);
      toast.success('Password changed!');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e) { toast.error(e?.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  const addUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) return toast.error('All fields required');
    setSaving(true);
    try {
      await api.post('/settings?action=users', newUser);
      toast.success('User created!');
      setUserModal(false);
    } catch { toast.error('Failed or email already exists'); } finally { setSaving(false); }
  };

  const setC = (k, v) => setCompany(c => ({ ...c, [k]: v }));

  return (
    <div>
      <div className="mb-5"><h1 className="text-xl font-black text-slate-800">⚙️ Settings</h1></div>
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg mb-5 w-fit border border-slate-200">
        {[['company','🏢 Company'],['users','👥 Users'],['password','🔐 Password']].map(([key,label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${tab === key ? 'bg-white shadow text-blue-600' : 'text-slate-500'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'company' && (
        <Card>
          <CardHeader title="Company Profile" subtitle="IEC, GSTIN, bank, contact details" />
          <div className="p-5">
            {compLoading ? <Spinner /> : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
                  <FormGroup label="Company Name" required><Input value={company.name || ''} onChange={e => setC('name', e.target.value)} /></FormGroup>
                  <FormGroup label="GSTIN"><Input value={company.gstin || ''} onChange={e => setC('gstin', e.target.value)} placeholder="27AAACM1234F1ZX" /></FormGroup>
                  <FormGroup label="IEC Number"><Input value={company.iec_no || ''} onChange={e => setC('iec_no', e.target.value)} placeholder="0512034567" /></FormGroup>
                  <FormGroup label="PAN"><Input value={company.pan || ''} onChange={e => setC('pan', e.target.value)} /></FormGroup>
                  <FormGroup label="AD Code"><Input value={company.ad_code || ''} onChange={e => setC('ad_code', e.target.value)} /></FormGroup>
                  <FormGroup label="Email"><Input type="email" value={company.email || ''} onChange={e => setC('email', e.target.value)} /></FormGroup>
                  <FormGroup label="Phone"><Input value={company.phone || ''} onChange={e => setC('phone', e.target.value)} /></FormGroup>
                  <FormGroup label="Financial Year Start"><Input type="date" value={company.fy_start || ''} onChange={e => setC('fy_start', e.target.value)} /></FormGroup>
                  <FormGroup label="Default Currency"><Select value={company.currency || 'INR'} onChange={e => setC('currency', e.target.value)}><option>INR</option><option>USD</option><option>EUR</option></Select></FormGroup>
                </div>
                <FormGroup label="Registered Address"><Textarea value={company.address || ''} onChange={e => setC('address', e.target.value)} /></FormGroup>
                <div className="mt-4 flex justify-end">
                  <Btn loading={saving} onClick={saveCompany}>💾 Save Settings</Btn>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {tab === 'users' && (
        <Card>
          <CardHeader title="User Management" actions={<Btn size="sm" onClick={() => setUserModal(true)}>+ Add User</Btn>} />
          {usersLoading ? <Spinner /> : (
            <Table headers={['Name', 'Email', 'Role', 'Status', 'Created']}>
              {(usersData?.users || []).map(u => (
                <Tr key={u.id}>
                  <Td><span className="font-bold">{u.name}</span></Td>
                  <Td>{u.email}</Td>
                  <Td><Badge color={u.role === 'admin' ? 'red' : u.role === 'manager' ? 'blue' : 'gray'}>{u.role}</Badge></Td>
                  <Td>{u.is_active ? <Badge color="green">Active</Badge> : <Badge color="red">Inactive</Badge>}</Td>
                  <Td>{fmtDate(u.created_at)}</Td>
                </Tr>
              ))}
            </Table>
          )}
          <Modal open={userModal} onClose={() => setUserModal(false)} title="Add User" size="sm"
            footer={<><Btn variant="ghost" onClick={() => setUserModal(false)}>Cancel</Btn><Btn loading={saving} onClick={addUser}>Create User</Btn></>}>
            <div className="space-y-3">
              <FormGroup label="Full Name" required><Input value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))} /></FormGroup>
              <FormGroup label="Email" required><Input type="email" value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} /></FormGroup>
              <FormGroup label="Password" required><Input type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} /></FormGroup>
              <FormGroup label="Role"><Select value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}><option value="staff">Staff</option><option value="manager">Manager</option><option value="admin">Admin</option></Select></FormGroup>
            </div>
          </Modal>
        </Card>
      )}

      {tab === 'password' && (
        <Card className="max-w-md">
          <CardHeader title="Change Password" />
          <div className="p-5 space-y-4">
            <FormGroup label="Current Password"><Input type="password" value={pwForm.current_password} onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} /></FormGroup>
            <FormGroup label="New Password" hint="Minimum 8 characters"><Input type="password" value={pwForm.new_password} onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} /></FormGroup>
            <FormGroup label="Confirm New Password"><Input type="password" value={pwForm.confirm_password} onChange={e => setPwForm(p => ({ ...p, confirm_password: e.target.value }))} /></FormGroup>
            <Btn loading={saving} onClick={changePassword}>🔐 Change Password</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}
